import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import { API_CONFIG, createApiUrl } from '../config/api';
import { syncConsentToBackend } from '../utils/consentSync';
import { clearConsentSyncFlags } from '../utils/pdConsent';
import { getReferralCode, clearReferralCode } from '../utils/referralStorage';
import { networkLocalAuthError } from '../services/localAuthApi';
import { setError } from './errorSlice';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { runLogoutCleanups } from './logoutCleanup';
import { DOCUMENT_STATUS_ACTION_PREFIX, DOCUMENT_INFO_REFRESH_ACTION_PREFIX } from './documentInfoActions';

export interface User {
  username: string;
  userId: string | number;
  balance: number;
  bonus: number;
  activeRewriteSlotId?: string | null;
  login?: string;
  email?: string;
  emailVerified?: boolean;
  authProvider?: string;
  /**
   * Состояние обучений (README_ONBOARDING_STATE_BACKEND.md). Нет у старого
   * бэкенда — тогда фронт хранит состояние в localStorage.
   */
  onboarding?: {
    rewriteTour?: import("../utils/onboardingState").RewriteTourState | null;
  } | null;
}

export type VersionCheckStatus = 'none' | 'pending' | 'processing' | 'ready' | 'failed';

export interface VersionCheck {
  reportId: string | null;
  checked: boolean;
  status: VersionCheckStatus;
  isReady: boolean;
  checkProvider?: 'antiplagiat_ru' | 'domate';
  originalityPercent?: number | null;
  aiPercent?: number | null;
  aiContentAbsent?: boolean;
}

export interface ParsedVersion {
  docNumber: number;
  fileName: string;
  lastModified: string;
  size: number;
  totalPrice: number;
  totalPriceAI: number;
  totalPriceWithReport: number;
  totalPriceAIWithReport: number;
  reportId: string | null;
  type: 'original' | 'paraphrased' | 'intermediate';
  /** true — обычный пользователь может отправить версию на проверку. */
  isLastVersion?: boolean;
  /** Статус проверки версии уникальности (см. REWRITE_CHECK_FRONTEND.md). */
  check?: VersionCheck;
}

/** Пайплайн режима «убрать ИИ» (README_REMOVE_AI_PIPELINE_FRONTEND.md). */
export type RemoveAiTarget = "antiplagiat" | "domate";

/**
 * Выбор пайплайна «убрать ИИ» для слота:
 * - locked: false — отчёта нет, пользователь выбирает сам;
 * - locked: true + target — система задана прикреплённым отчётом;
 * - locked: true + target: null — отчёт есть, но система не распознана.
 */
export interface RemoveAiInfo {
  target: RemoveAiTarget | null;
  locked: boolean;
}

export const REMOVE_AI_TARGET_LABELS: Record<RemoveAiTarget, string> = {
  antiplagiat: "Антиплагиат",
  domate: "Domate",
};

export interface DocumentStatusFlags {
  uploaded: boolean;
  parsed: boolean;
  paraphrased: boolean;
  built: boolean;
}

export interface DocumentInfo {
  slotId?: string;
  hasDocument: boolean;
  documentStatus?: 'empty' | 'uploaded' | 'parsed' | 'paraphrased' | 'built';
  statusFlags?: DocumentStatusFlags;
  documentName?: string;
  extractedFilesCount?: number;
  lastModified?: string;
  parsedVersions?: ParsedVersion[];
  totalVersions?: number;
  /** Нет у старого бэкенда — тогда выбор пайплайна в диалоге не показываем. */
  removeAi?: RemoveAiInfo | null;
}

interface AuthState {
  isAuthenticated: boolean;
  isAdministrator: boolean;
  user: User | null;
  // accessToken: string | null;
  // refreshToken: string | null;
  documentInfo: DocumentInfo | null;
  loading: boolean;
  loadingDocumentStatus: boolean;
  refreshTimeoutId?: number | null;
  /** Источник текущего refresh (для UX: не блокировать UI при тихом обновлении). */
  refreshFrom: "verify" | "fetch" | "scheduled" | "visibility" | null;
  /** Инкремент при каждой смене accessToken — переподключение сокета. */
  tokenVersion: number;
  /** Фоновый refresh (без полноэкранного loading в Auth). */
  isRefreshingToken: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isAdministrator: false,
  user: null,
  // accessToken: null,
  // refreshToken: null,
  documentInfo: null,
  loading: true,
  loadingDocumentStatus: false,
  refreshTimeoutId: null,
  refreshFrom: null,
  tokenVersion: 0,
  isRefreshingToken: false,
};

export const initializeFromStorage = createAsyncThunk(
  'auth/initializeFromStorage',
  async (_, { dispatch }) => {
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    if (storedAccessToken && storedUser) {
      // dispatch(setTokens({ accessToken: storedAccessToken, refreshToken: null }));
      dispatch(setUser(JSON.parse(storedUser)));
      return true;
    }
    return false;
  }
);

export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (_, { getState, dispatch, rejectWithValue }) => {
    // const state = getState() as RootState;
    // const accessToken = state.storeAuth.accessToken;
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return rejectWithValue('no-token');
    try {
      const response = await fetchWithTimeout(createApiUrl(API_CONFIG.ENDPOINTS.AUTH.VERIFY), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        dispatch(setUser(data));
        const { scheduleTokenRefresh } = await import('../utils/scheduleTokenRefresh');
        dispatch(scheduleTokenRefresh());
        return true;
      } else if (response.status === 401) {
        const refreshed = await dispatch(refreshAccessToken("verify")).unwrap();
        if (refreshed) {
          return true;
        }
      }
      await dispatch(logout());
      return rejectWithValue('invalid-token');
    } catch (e) {
      await dispatch(logout());
      return rejectWithValue('verify-error');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (
    payload: { username: string; password: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await fetch(createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGIN), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData?.message || 'login-failed');
      }
      const data = await response.json();
      // dispatch(setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken }));
      dispatch(setUser(data));
      dispatch(setIsAuthenticated(true));
      localStorage.setItem('accessToken', data.accessToken);
      // localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      syncConsentToBackend();
      return true;
    } catch (e) {
      return rejectWithValue('login-error');
    }
  }
);

export const loginWithToken = createAsyncThunk(
  'auth/loginWithToken',
  async (
    payload: { token: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await fetch(createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGIN), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: "include"
      });
      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData?.message || 'token-login-failed');
      }
      const data = await response.json();
      // dispatch(setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken }));
      dispatch(setIsAuthenticated(true));
      dispatch(setUser(data));
      localStorage.setItem('accessToken', data.accessToken);
      // localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      syncConsentToBackend();
      return true;
    } catch (e) {
      return rejectWithValue('token-login-error');
    }
  }
);

export const loginWithLocal = createAsyncThunk(
  'auth/loginWithLocal',
  async (
    payload: { login: string; password: string; captchaToken?: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { loginLocal } = await import('../services/localAuthApi');
      const data = await loginLocal(payload);

      dispatch(setIsAuthenticated(true));
      dispatch(setUser(data));
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      syncConsentToBackend();

      const { scheduleTokenRefresh } = await import('../utils/scheduleTokenRefresh');
      dispatch(scheduleTokenRefresh());

      return { isNewUser: data.isNewUser };
    } catch (e: any) {
      if (e?.name === 'LocalAuthApiError') {
        return rejectWithValue(e.toPayload ? e.toPayload() : {
          message: e.message,
          status: e.status,
          errorCode: e.errorCode,
          field: e.field,
          emailVerificationRequired: e.emailVerificationRequired,
          retryAfter: e.retryAfter,
        });
      }
      return rejectWithValue(networkLocalAuthError().toPayload());
    }
  }
);

export const verifyLocalEmail = createAsyncThunk(
  'auth/verifyLocalEmail',
  async (token: string, { dispatch, rejectWithValue }) => {
    try {
      const { verifyLocalEmail: verifyEmailApi } = await import('../services/localAuthApi');
      const data = await verifyEmailApi(token);

      dispatch(setIsAuthenticated(true));
      dispatch(setUser(data));
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      syncConsentToBackend();

      const { scheduleTokenRefresh } = await import('../utils/scheduleTokenRefresh');
      dispatch(scheduleTokenRefresh());

      return { isNewUser: data.isNewUser };
    } catch (e: any) {
      if (e?.name === 'LocalAuthApiError') {
        return rejectWithValue(e.toPayload ? e.toPayload() : {
          message: e.message,
          status: e.status,
          errorCode: e.errorCode,
          field: e.field,
          retryAfter: e.retryAfter,
        });
      }
      return rejectWithValue(networkLocalAuthError().toPayload());
    }
  }
);

export const resetLocalPassword = createAsyncThunk(
  'auth/resetLocalPassword',
  async (
    payload: { token: string; password: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { resetLocalPassword: resetApi } = await import('../services/localAuthApi');
      const data = await resetApi(payload.token, payload.password);

      dispatch(setIsAuthenticated(true));
      dispatch(setUser(data));
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      syncConsentToBackend();

      const { scheduleTokenRefresh } = await import('../utils/scheduleTokenRefresh');
      dispatch(scheduleTokenRefresh());

      return { isNewUser: data.isNewUser };
    } catch (e: any) {
      if (e?.name === 'LocalAuthApiError') {
        return rejectWithValue(e.toPayload ? e.toPayload() : {
          message: e.message,
          status: e.status,
          errorCode: e.errorCode,
          field: e.field,
          retryAfter: e.retryAfter,
        });
      }
      return rejectWithValue(networkLocalAuthError().toPayload());
    }
  }
);

export const loginWithTelegram = createAsyncThunk(
  'auth/loginWithTelegram',
  async (
    telegramData: ITelegramData,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const referralCode = getReferralCode();
      const body = referralCode
        ? { ...telegramData, referralCode }
        : telegramData;
      const response = await fetch(createApiUrl(API_CONFIG.ENDPOINTS.AUTH.TELEGRAM), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        return rejectWithValue(data?.message || 'telegram-login-failed');
      }
      
      dispatch(setIsAuthenticated(true));
      dispatch(setUser(data));
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      clearReferralCode();
      syncConsentToBackend();
      return { isNewUser: data.isNewUser };
    } catch (e) {
      return rejectWithValue('telegram-login-error');
    }
  }
);

export type RefreshReason = AuthState['refreshFrom'];

const isSilentRefresh = (reason: RefreshReason) =>
  reason === 'fetch' || reason === 'scheduled' || reason === 'visibility';

export const refreshAccessToken = createAsyncThunk(
  'auth/refresh',
  async (refreshFrom: RefreshReason, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setRefreshFrom(refreshFrom));
      const response = await fetch(createApiUrl(API_CONFIG.ENDPOINTS.AUTH.REFRESH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        await dispatch(logout());
        return rejectWithValue('refresh-failed');
      }
      const data = await response.json();
      dispatch(setIsAuthenticated(true));
      dispatch(setUser(data));
      localStorage.setItem('accessToken', data.accessToken);
      dispatch(bumpTokenVersion());

      const { scheduleTokenRefresh } = await import('../utils/scheduleTokenRefresh');
      dispatch(scheduleTokenRefresh());

      // В сборке «только проверки» сокета нет — и модуль сокета в неё не попадает
      if (process.env.REACT_APP_EDITION !== 'checks') {
        const { reconnectSocketWithCurrentToken } = await import('./socketSlice');
        await dispatch(reconnectSocketWithCurrentToken());
      }

      return true;
    } catch (e) {
      await dispatch(logout());
      return rejectWithValue('refresh-error');
    } finally {
      dispatch(setRefreshFrom(null));
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch, getState }) => {
  const userId = (getState() as RootState).storeAuth.user?.userId;

  try {
    await fetchWithTimeout(createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGOUT), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
  } catch {}
  const { clearScheduledTokenRefresh } = await import('../utils/scheduleTokenRefresh');
  dispatch(clearScheduledTokenRefresh());
  // Полный сброс всех Redux-слайсов при выходе
  // (socket сбрасывается через useSocket hook при isAuthenticated → false).
  // Слайсы конкретной сборки сбрасывает её store через registerLogoutCleanup.
  dispatch(clearAuth());
  dispatch(setError(null));
  runLogoutCleanups(dispatch, userId);
  clearConsentSyncFlags();
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
});

const authSlice = createSlice({
  name: 'storeAuth',
  initialState,
  reducers: {
    // setTokens: (
    //   state,
    //   action: PayloadAction<{ accessToken: string | null; refreshToken: string | null }>
    // ) => {
    //   state.accessToken = action.payload.accessToken;
    //   state.refreshToken = action.payload.refreshToken;
    //   state.isAuthenticated = Boolean(action.payload.accessToken);
    // },
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setUser: (state, action: PayloadAction<any | null>) => {
      state.user = action.payload.user;
      state.isAdministrator = action.payload.isAdministrator;
    },
    setDocumentInfo: (state, action: PayloadAction<DocumentInfo | null>) => {
      state.documentInfo = action.payload;
    },
    setUserBalance: (
      state,
      action: PayloadAction<{ balance: number; bonus?: number }>
    ) => {
      if (state.user) {
        state.user.balance = action.payload.balance;
        if (typeof action.payload.bonus === "number") {
          state.user.bonus = action.payload.bonus;
        }
        try {
          localStorage.setItem(
            "user",
            JSON.stringify({ ...state.user })
          );
        } catch {}
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoadingDocumentStatus: (state, action: PayloadAction<boolean>) => {
      state.loadingDocumentStatus = action.payload;
    },
    clearAuth: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      // state.accessToken = null;
      // state.refreshToken = null;
      state.documentInfo = null;
      state.loading = false;
      state.loadingDocumentStatus = false;
      if (state.refreshTimeoutId) {
        window.clearTimeout(state.refreshTimeoutId);
        state.refreshTimeoutId = null;
      }
    },
    setRefreshTimeoutId: (state, action: PayloadAction<number | null>) => {
      state.refreshTimeoutId = action.payload ?? null;
    },
    setRefreshFrom: (state, action: PayloadAction<RefreshReason>) => {
      state.refreshFrom = action.payload;
    },
    bumpTokenVersion: (state) => {
      state.tokenVersion += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeFromStorage.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeFromStorage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initializeFromStorage.rejected, (state) => {
        state.loading = false;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loginWithToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginWithToken.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(loginWithToken.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loginWithTelegram.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginWithTelegram.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(loginWithTelegram.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loginWithLocal.fulfilled, (state) => {
        state.isAuthenticated = true;
      })
      .addCase(verifyLocalEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyLocalEmail.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(verifyLocalEmail.rejected, (state) => {
        state.loading = false;
      })
      .addCase(resetLocalPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetLocalPassword.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(resetLocalPassword.rejected, (state) => {
        state.loading = false;
      })
      .addCase(verifyToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyToken.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(verifyToken.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
      })
      .addCase(refreshAccessToken.pending, (state, action) => {
        const silent = isSilentRefresh(action.meta.arg);
        state.isRefreshingToken = true;
        if (!silent) {
          state.loading = true;
        }
      })
      .addCase(refreshAccessToken.fulfilled, (state) => {
        state.loading = false;
        state.isRefreshingToken = false;
      })
      .addCase(refreshAccessToken.rejected, (state) => {
        state.loading = false;
        state.isRefreshingToken = false;
      })
      // Thunk-и статуса документа живут в documentInfoThunks.ts (только
      // основная сборка) — здесь ловим их экшены по типу, не импортируя их
      .addMatcher(
        (action) => action.type === `${DOCUMENT_INFO_REFRESH_ACTION_PREFIX}/pending`,
        (state) => {
          state.loading = true;
        }
      )
      .addMatcher(
        (action) =>
          action.type === `${DOCUMENT_INFO_REFRESH_ACTION_PREFIX}/fulfilled` ||
          action.type === `${DOCUMENT_INFO_REFRESH_ACTION_PREFIX}/rejected`,
        (state) => {
          state.loading = false;
        }
      )
      .addMatcher(
        (action) => action.type === `${DOCUMENT_STATUS_ACTION_PREFIX}/pending`,
        (state) => {
          state.loadingDocumentStatus = true;
        }
      )
      .addMatcher(
        (action) =>
          action.type === `${DOCUMENT_STATUS_ACTION_PREFIX}/fulfilled` ||
          action.type === `${DOCUMENT_STATUS_ACTION_PREFIX}/rejected`,
        (state) => {
          state.loadingDocumentStatus = false;
        }
      );
  },
});

export const { 
  // setTokens, 
  setIsAuthenticated,
  setUser, 
  setDocumentInfo, 
  setUserBalance,
  setLoading, 
  setLoadingDocumentStatus, 
  clearAuth, 
  setRefreshTimeoutId,
  setRefreshFrom,
  bumpTokenVersion,
} = authSlice.actions;

export const selectAuth = (state: RootState) => state.storeAuth;
export const selectIsAdministrator = (state: RootState) => state.storeAuth.isAdministrator;
export const selectUser = (state: RootState) => state.storeAuth.user;
export const selectRefreshFrom = (state: RootState) => state.storeAuth.refreshFrom;
export const selectTokenVersion = (state: RootState) => state.storeAuth.tokenVersion;
export const selectIsRefreshingToken = (state: RootState) => state.storeAuth.isRefreshingToken;

export default authSlice.reducer;
